import { get, off, push, ref, update } from "firebase/database";
import { listenDatabaseValue as onValue } from "../utils/listenDatabaseValue";
import { getPathDatabaseByRole } from "./auth";
import { db } from "../lib/firebase";
import { LaunchError } from "../utils/errors";
import { deleteFolderAndUnlinkContents } from "./folderDeletion";
import {
  CONTENT_FOLDER_ROOT_ID,
  CREATIVE_TEMPLATE_FOLDER_CHILDREN_ROOT,
  CREATIVE_TEMPLATE_FOLDER_CONTENTS_ROOT,
  CREATIVE_TEMPLATE_FOLDERS_ROOT,
} from "../utils/constants";
import {
  isDuplicateFolderName,
  normalizeFolderName,
  normalizeParentFolderID,
} from "../utils/contentFolderUtils";

const getCreativeTemplateFoldersPath = ({ advertiserID }) =>
  `${CREATIVE_TEMPLATE_FOLDERS_ROOT}/${advertiserID}`;

const getCreativeTemplateFolderPath = ({ advertiserID, folderID }) =>
  `${getCreativeTemplateFoldersPath({ advertiserID })}/${folderID}`;

const getCreativeTemplateFolderChildrenPath = ({
  advertiserID,
  parentFolderID,
}) =>
  `${CREATIVE_TEMPLATE_FOLDER_CHILDREN_ROOT}/${advertiserID}/${parentFolderID}`;

export const getCreativeTemplateFolderContentIndexPath = ({
  advertiserID,
  folderID,
  contentID,
}) =>
  `${CREATIVE_TEMPLATE_FOLDER_CONTENTS_ROOT}/${advertiserID}/${folderID}/${contentID}`;

const getCreativeTemplateCardPath = ({ advertiserID, templateID }) =>
  `creativeTemplatesCards/${advertiserID}/${templateID}`;

const getCreativeTemplateRecordPath = ({ advertiserID, templateID }) =>
  `creativeTemplates/${advertiserID}/${templateID}`;

export const getCreativeTemplateFolderIndexUpdates = ({
  advertiserID,
  templateID,
  folderID,
}) => {
  if (!advertiserID || !templateID || !folderID) return {};

  return {
    [getCreativeTemplateFolderContentIndexPath({
      advertiserID,
      folderID,
      contentID: templateID,
    })]: true,
  };
};

export const startListenerCreativeTemplateFolders =
  ({ onUpdatedDataCallback = () => {}, onReferenceAvailable = () => {} }) =>
  async (dispatch) => {
    try {
      const { userID: advertiserID } = dispatch(getPathDatabaseByRole());

      if (!advertiserID) {
        onUpdatedDataCallback({});
        return false;
      }

      const dbRef = ref(db, getCreativeTemplateFoldersPath({ advertiserID }));
      onReferenceAvailable(dbRef);
      onValue(dbRef, (snapshot) => {
        onUpdatedDataCallback(snapshot.val() || {});
      });

      return true;
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startListenerCreativeTemplateFolders",
          tags: { action: "creativeTemplateFolders" },
          level: "error",
        })
      );
      return false;
    }
  };

export const stopListenerCreativeTemplateFolders =
  ({ listenerRef } = {}) =>
  async () => {
    if (!listenerRef) return;
    await off(listenerRef);
  };

export const startCreateCreativeTemplateFolder =
  ({ name, parentFolderID = CONTENT_FOLDER_ROOT_ID }) =>
  async (dispatch) => {
    const normalizedName = normalizeFolderName(name);
    const normalizedParentID = normalizeParentFolderID(parentFolderID);

    try {
      const { userID: advertiserID } = dispatch(getPathDatabaseByRole());

      if (!advertiserID || !normalizedName) {
        return { ok: false, reason: "invalidName" };
      }

      const foldersPath = getCreativeTemplateFoldersPath({ advertiserID });
      const foldersSnapshot = await get(ref(db, foldersPath));
      const folders = Object.entries(foldersSnapshot.val() || {}).map(
        ([id, folder]) => ({ id, ...folder })
      );

      if (
        isDuplicateFolderName({
          folders,
          parentFolderID: normalizedParentID,
          name: normalizedName,
        })
      ) {
        return { ok: false, reason: "duplicateName" };
      }

      const folderID = push(ref(db, foldersPath)).key;
      const now = Date.now();
      const folder = {
        name: normalizedName,
        parentFolderID: normalizedParentID,
        creationTime: now,
        updatedTime: now,
      };

      await update(ref(db), {
        [getCreativeTemplateFolderPath({ advertiserID, folderID })]: folder,
        [`${getCreativeTemplateFolderChildrenPath({
          advertiserID,
          parentFolderID: normalizedParentID,
        })}/${folderID}`]: true,
      });

      return {
        ok: true,
        data: {
          id: folderID,
          ...folder,
        },
      };
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startCreateCreativeTemplateFolder",
          context: { name: normalizedName, parentFolderID: normalizedParentID },
          tags: { action: "creativeTemplateFolders" },
          level: "error",
        })
      );
      return { ok: false };
    }
  };

export const startMoveCreativeTemplatesToFolder =
  ({ contentIDs, folderID }) =>
  async (dispatch) => {
    try {
      const { userID: advertiserID } = dispatch(getPathDatabaseByRole());
      const uniqueContentIDs = [
        ...new Set((contentIDs || []).filter(Boolean).map(String)),
      ];

      if (!advertiserID || !folderID || uniqueContentIDs.length === 0) {
        return { ok: false };
      }

      const targetFolderSnapshot = await get(
        ref(db, getCreativeTemplateFolderPath({ advertiserID, folderID }))
      );
      if (!targetFolderSnapshot.exists()) {
        return { ok: false, reason: "folderNotFound" };
      }

      const contentSnapshots = await Promise.all(
        uniqueContentIDs.map(async (contentID) => {
          const snapshot = await get(
            ref(
              db,
              getCreativeTemplateCardPath({
                advertiserID,
                templateID: contentID,
              })
            )
          );
          return {
            contentID,
            content: snapshot.exists() ? snapshot.val() : null,
          };
        })
      );

      const updates = {};
      const movedContentIDs = [];

      contentSnapshots.forEach(({ contentID, content }) => {
        if (!content) return;

        const previousFolderID = content.folderID;
        if (previousFolderID === folderID) return;

        if (previousFolderID) {
          updates[
            getCreativeTemplateFolderContentIndexPath({
              advertiserID,
              folderID: previousFolderID,
              contentID,
            })
          ] = null;
        }

        updates[
          getCreativeTemplateFolderContentIndexPath({
            advertiserID,
            folderID,
            contentID,
          })
        ] = true;
        updates[
          `${getCreativeTemplateCardPath({
            advertiserID,
            templateID: contentID,
          })}/folderID`
        ] = folderID;
        updates[
          `${getCreativeTemplateRecordPath({
            advertiserID,
            templateID: contentID,
          })}/folderID`
        ] = folderID;
        movedContentIDs.push(contentID);
      });

      if (movedContentIDs.length === 0) {
        return { ok: false, reason: "noChanges" };
      }

      await update(ref(db), updates);

      return {
        ok: true,
        data: {
          contentIDs: movedContentIDs,
          folderID,
        },
      };
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startMoveCreativeTemplatesToFolder",
          context: { contentIDs, folderID },
          tags: { action: "creativeTemplateFolders" },
          level: "error",
        })
      );
      return { ok: false };
    }
  };

export const startUnassignCreativeTemplatesFromFolder =
  ({ contentIDs }) =>
  async (dispatch) => {
    try {
      const { userID: advertiserID } = dispatch(getPathDatabaseByRole());
      const uniqueContentIDs = [
        ...new Set((contentIDs || []).filter(Boolean).map(String)),
      ];

      if (!advertiserID || uniqueContentIDs.length === 0) {
        return { ok: false };
      }

      const contentSnapshots = await Promise.all(
        uniqueContentIDs.map(async (contentID) => {
          const snapshot = await get(
            ref(
              db,
              getCreativeTemplateCardPath({
                advertiserID,
                templateID: contentID,
              })
            )
          );
          return {
            contentID,
            content: snapshot.exists() ? snapshot.val() : null,
          };
        })
      );

      const updates = {};
      const unassignedContentIDs = [];

      contentSnapshots.forEach(({ contentID, content }) => {
        if (!content?.folderID) return;

        updates[
          getCreativeTemplateFolderContentIndexPath({
            advertiserID,
            folderID: content.folderID,
            contentID,
          })
        ] = null;
        updates[
          `${getCreativeTemplateCardPath({
            advertiserID,
            templateID: contentID,
          })}/folderID`
        ] = null;
        updates[
          `${getCreativeTemplateRecordPath({
            advertiserID,
            templateID: contentID,
          })}/folderID`
        ] = null;
        unassignedContentIDs.push(contentID);
      });

      if (unassignedContentIDs.length === 0) {
        return { ok: false, reason: "noChanges" };
      }

      await update(ref(db), updates);

      return {
        ok: true,
        data: {
          contentIDs: unassignedContentIDs,
        },
      };
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startUnassignCreativeTemplatesFromFolder",
          context: { contentIDs },
          tags: { action: "creativeTemplateFolders" },
          level: "error",
        })
      );
      return { ok: false };
    }
  };

export const startRenameCreativeTemplateFolder =
  ({ folderID, name }) =>
  async (dispatch) => {
    const normalizedName = normalizeFolderName(name);

    try {
      const { userID: advertiserID } = dispatch(getPathDatabaseByRole());

      if (!advertiserID || !folderID || !normalizedName) {
        return { ok: false, reason: "invalidName" };
      }

      const folderSnapshot = await get(
        ref(db, getCreativeTemplateFolderPath({ advertiserID, folderID }))
      );

      if (!folderSnapshot.exists()) {
        return { ok: false, reason: "folderNotFound" };
      }

      const currentFolder = folderSnapshot.val();
      const foldersPath = getCreativeTemplateFoldersPath({ advertiserID });
      const foldersSnapshot = await get(ref(db, foldersPath));
      const folders = Object.entries(foldersSnapshot.val() || {}).map(
        ([id, folder]) => ({ id, ...folder })
      );

      if (
        isDuplicateFolderName({
          folders,
          parentFolderID: currentFolder.parentFolderID,
          name: normalizedName,
          excludeFolderID: folderID,
        })
      ) {
        return { ok: false, reason: "duplicateName" };
      }

      const now = Date.now();
      await update(ref(db), {
        [`${getCreativeTemplateFolderPath({ advertiserID, folderID })}/name`]:
          normalizedName,
        [`${getCreativeTemplateFolderPath({ advertiserID, folderID })}/updatedTime`]:
          now,
      });

      return {
        ok: true,
        data: {
          id: folderID,
          name: normalizedName,
        },
      };
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startRenameCreativeTemplateFolder",
          context: { folderID, name: normalizedName },
          tags: { action: "creativeTemplateFolders" },
          level: "error",
        })
      );
      return { ok: false };
    }
  };

export const startDeleteCreativeTemplateFolder =
  ({ folderID }) =>
  async (dispatch) => {
    try {
      const { userID: advertiserID } = dispatch(getPathDatabaseByRole());

      return await deleteFolderAndUnlinkContents({
        advertiserID,
        folderID,
        foldersRoot: CREATIVE_TEMPLATE_FOLDERS_ROOT,
        childrenRoot: CREATIVE_TEMPLATE_FOLDER_CHILDREN_ROOT,
        contentsRoot: CREATIVE_TEMPLATE_FOLDER_CONTENTS_ROOT,
        getEntityPaths: (id) => [
          getCreativeTemplateCardPath({ advertiserID, templateID: id }),
          getCreativeTemplateRecordPath({ advertiserID, templateID: id }),
        ],
      });
    } catch (error) {
      dispatch(
        LaunchError(error, false, [], {
          functionName: "startDeleteCreativeTemplateFolder",
          context: { folderID },
          tags: { action: "creativeTemplateFolders" },
          level: "error",
        })
      );
      return { ok: false };
    }
  };
